import React from "react";
import {
    SectionCard,
    PaginationBar,
    inputClass,
    mutedButtonClass,
    primaryButtonClass,
    saveButtonClass,
    dangerButtonClass,
    formatDate,
} from "../adminShared";

const DataReviewTab = ({
    working,
    pipelineStatus,
    pipelineState,
    nextGlassIdInput,
    setNextGlassIdInput,
    pipelineRuns,
    pipelineRunsPagination,
    goToPipelineRunsPage,
    deletePipelineRun,
    triggerPipeline,
    stopPipeline,
    updateLastGlassId,
    refreshPendingWindowGlass,
    approveAllPendingWindowGlass,
    deleteAllPendingWindowGlass,
    toggleSelectAllPendingWindowGlass,
    reviewBulkWindowGlass,
    reviewSingleWindowGlass,
    pendingWindowGlass,
    selectedPendingIds,
    pendingWindowGlassPagination,
    goToPendingWindowGlassPage,
    togglePendingSelect,
}) => (
    <div className="flex flex-col gap-6">
        <SectionCard
            title="Pipeline Trigger"
            description="Run monthly data refresh manually and control incremental glass scraper start id."
            right={
                <div className="flex gap-2">
                    <button
                        className={mutedButtonClass}
                        onClick={stopPipeline}
                        disabled={working || !pipelineStatus?.is_running}
                    >
                        Stop pipeline
                    </button>
                    <button
                        className={primaryButtonClass}
                        onClick={triggerPipeline}
                        disabled={working || pipelineStatus?.is_running}
                    >
                        Run pipeline now
                    </button>
                </div>
            }
        >
            <p className="mb-3 text-xs text-slate-500">
                Pipeline status: {pipelineStatus?.is_running ? "Running" : "Idle"}
                {pipelineStatus?.stop_requested ? " (stop requested)" : ""}
                {pipelineStatus?.current_run?.started_at
                    ? ` | started: ${formatDate(pipelineStatus.current_run.started_at)}`
                    : ""}
            </p>
            <p className="mb-3 text-sm text-slate-500 text-left">
                Use the input below to set the last scraped WERS glazing ID for the incremental scraper.
            </p>
            <div className="grid gap-3 sm:grid-cols-[220px_140px]">
                <input
                    className={inputClass}
                    value={nextGlassIdInput}
                    onChange={(e) => setNextGlassIdInput(e.target.value)}
                    placeholder="glass last id"
                />
                <button className={mutedButtonClass} onClick={updateLastGlassId} disabled={working}>
                    Update ID
                </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
                Current Last WERS Glazing ID: {pipelineState?.glass_last_id ?? 0}
            </p>
            <div className="mt-4 rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="px-3 py-2">Run ID</th>
                            <th className="px-3 py-2">Trigger</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Started At</th>
                            <th className="px-3 py-2">Finished At</th>
                            <th className="px-3 py-2">Message</th>
                            <th className="px-3 py-2">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                        {pipelineRuns.map((run) => (
                            <tr key={run.pipeline_run_id}>
                                <td className="px-3 py-2">{run.pipeline_run_id}</td>
                                <td className="px-3 py-2">{run.trigger_type}</td>
                                <td className="px-3 py-2">{run.status}</td>
                                <td className="px-3 py-2">{formatDate(run.started_at)}</td>
                                <td className="px-3 py-2">{formatDate(run.finished_at)}</td>
                                <td className="px-3 py-2 max-w-[32rem] whitespace-normal break-words text-slate-600">
                                    {run.message || "-"}
                                </td>
                                <td className="px-3 py-2">
                                    <button
                                        className={dangerButtonClass}
                                        onClick={() => deletePipelineRun(run.pipeline_run_id)}
                                        disabled={working || run.status === "running"}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <PaginationBar
                page={pipelineRunsPagination.page}
                totalPages={pipelineRunsPagination.total_pages}
                totalCount={pipelineRunsPagination.total_count}
                totalLabel="runs"
                onPrevious={() => goToPipelineRunsPage(pipelineRunsPagination.page - 1)}
                onNext={() => goToPipelineRunsPage(pipelineRunsPagination.page + 1)}
                disabled={working}
            />
        </SectionCard>

        <SectionCard
            title="Window Glass Pending Review"
            description="Approve/reject new rows before they become visible to users."
            right={
                <div className="flex flex-wrap gap-2">
                    <button
                        className={mutedButtonClass}
                        onClick={() => refreshPendingWindowGlass()}
                        disabled={working}
                    >
                        Refresh table
                    </button>
                    <button
                        className={saveButtonClass}
                        onClick={approveAllPendingWindowGlass}
                        disabled={working || pendingWindowGlassPagination.total_count === 0}
                    >
                        Approve all pending
                    </button>
                    <button
                        className={mutedButtonClass}
                        onClick={deleteAllPendingWindowGlass}
                        disabled={working || pendingWindowGlassPagination.total_count === 0}
                    >
                        Delete all pending
                    </button>
                    <button
                        className={mutedButtonClass}
                        onClick={toggleSelectAllPendingWindowGlass}
                        disabled={working || pendingWindowGlass.length === 0}
                    >
                        {pendingWindowGlass.length > 0 &&
                            pendingWindowGlass.every((row) => selectedPendingIds.includes(row.window_glass_id))
                            ? "Clear selection"
                            : "Select all on page"}
                    </button>
                    <button
                        className={mutedButtonClass}
                        onClick={() => reviewBulkWindowGlass("approved")}
                        disabled={working}
                    >
                        Approve selected
                    </button>
                    <button
                        className={mutedButtonClass}
                        onClick={() => reviewBulkWindowGlass("rejected")}
                        disabled={working}
                    >
                        Reject selected
                    </button>
                </div>
            }
        >
            <div className="max-w-[120rem] overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full table-auto whitespace-nowrap text-left text-sm leading-5">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="px-3 py-2 whitespace-nowrap">Select</th>
                            <th className="px-3 py-2 whitespace-nowrap">ID</th>
                            <th className="px-3 py-2 whitespace-nowrap">Window Glass Name</th>
                            <th className="px-3 py-2 whitespace-nowrap">Window Name</th>
                            <th className="px-3 py-2 whitespace-nowrap">Manufacturer</th>
                            <th className="px-3 py-2 whitespace-nowrap">Glass</th>
                            <th className="px-3 py-2 whitespace-nowrap">Supplier</th>
                            <th className="px-3 py-2 whitespace-nowrap">Operator Type</th>
                            <th className="px-3 py-2 whitespace-nowrap">Frame Material</th>
                            <th className="px-3 py-2 whitespace-nowrap">U</th>
                            <th className="px-3 py-2 whitespace-nowrap">S</th>
                            <th className="px-3 py-2 whitespace-nowrap">V</th>
                            <th className="px-3 py-2 whitespace-nowrap">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                        {pendingWindowGlass.map((row) => (
                            <tr key={row.window_glass_id}>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={selectedPendingIds.includes(row.window_glass_id)}
                                        onChange={(e) => togglePendingSelect(row.window_glass_id, e.target.checked)}
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.window_glass_id}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.window_glass_name || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.window_name || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.manufacturer || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.glass || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.supplier || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.operator_type || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.frame_material || "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.u ?? "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.s ?? "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{row.v ?? "-"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="flex gap-2">
                                        <button
                                            className={saveButtonClass}
                                            onClick={() => reviewSingleWindowGlass(row.window_glass_id, "approved")}
                                            disabled={working}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            className={mutedButtonClass}
                                            onClick={() => reviewSingleWindowGlass(row.window_glass_id, "rejected")}
                                            disabled={working}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {pendingWindowGlass.length === 0 ? (
                            <tr>
                                <td className="px-3 py-8 text-center text-slate-500 whitespace-nowrap" colSpan={13}>
                                    No pending rows.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <PaginationBar
                page={pendingWindowGlassPagination.page}
                totalPages={pendingWindowGlassPagination.total_pages}
                totalCount={pendingWindowGlassPagination.total_count}
                totalLabel="rows"
                onPrevious={() => goToPendingWindowGlassPage(pendingWindowGlassPagination.page - 1)}
                onNext={() => goToPendingWindowGlassPage(pendingWindowGlassPagination.page + 1)}
                disabled={working}
            />
        </SectionCard>
    </div>
);

export default DataReviewTab;

